import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ATT } from "../constants/testIds";
import { Camera, MapPinArea, CheckCircle, ArrowRight, XCircle, ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

function fmtTime(iso) { return iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"; }

export default function CheckIn() {
  const { user } = useAuth();  // eslint-disable-line no-unused-vars
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selfieBlob, setSelfieBlob] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [videoOn, setVideoOn] = useState(false);
  const [history, setHistory] = useState([]);
  const [baseline, setBaseline] = useState({ enrolled: null });
  const [enrollMode, setEnrollMode] = useState(false);
  const [lastMatch, setLastMatch] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/attendance/today").then(({ data }) => setToday(data)).finally(() => setLoading(false));
    api.get("/attendance/mine", { params: { days: 30 } }).then(({ data }) => setHistory(data));
    api.get("/selfie/baseline/status").then(({ data }) => setBaseline(data)).catch(() => {});
  };
  useEffect(() => { load(); return () => stopVideo(); /* eslint-disable-next-line */ }, []);

  // Attach the camera stream once the <video> element is actually mounted.
  useEffect(() => {
    if (videoOn && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [videoOn]);

  const startVideo = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("This browser can't access the camera. Use Chrome/Safari over HTTPS, or check in without a selfie.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      setVideoOn(true); // mount <video> first — the effect below attaches the stream
    } catch (e) {
      const name = e?.name || "";
      const msg =
        name === "NotAllowedError" || name === "SecurityError"
          ? "Camera permission denied. Allow camera access in your browser/site settings, then try again."
          : name === "NotFoundError" || name === "OverconstrainedError"
          ? "No camera found on this device. You can still check in without a selfie."
          : name === "NotReadableError"
          ? "Your camera is being used by another app. Close it and try again."
          : "Camera unavailable. You can still check in without a selfie.";
      toast.error(msg);
    }
  };

  const stopVideo = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setVideoOn(false);
  };

  const captureSelfie = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const w = videoRef.current.videoWidth, h = videoRef.current.videoHeight;
    canvasRef.current.width = w; canvasRef.current.height = h;
    canvasRef.current.getContext("2d").drawImage(videoRef.current, 0, 0, w, h);
    const blob = await new Promise((r) => canvasRef.current.toBlob(r, "image/jpeg", 0.85));
    setSelfieBlob(blob);
    setSelfiePreview(URL.createObjectURL(blob));
    stopVideo();
  };

  const getLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      (e) => reject(e),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  });

  const uploadSelfie = async () => {
    if (!selfieBlob) return null;
    const fd = new FormData();
    fd.append("file", selfieBlob, `selfie-${Date.now()}.jpg`);
    const { data } = await api.post("/uploads?purpose=selfie", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  const enrollBaseline = async () => {
    if (!selfieBlob) { toast.error("Capture a clear front-facing photo first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", selfieBlob, `baseline-${Date.now()}.jpg`);
      await api.post("/selfie/baseline", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Baseline selfie saved. Face-match verification is now active.");
      setEnrollMode(false); setSelfieBlob(null); setSelfiePreview(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doAction = async (action) => {
    setBusy(true);
    setLastMatch(null);
    try {
      const loc = await getLocation();
      let selfie_path = null;
      try { selfie_path = await uploadSelfie(); } catch (_) {}
      const { data } = await api.post(`/attendance/${action.replace("_", "-")}`, {
        lat: loc.lat, lng: loc.lng, selfie_path,
      });
      const addressLine = data.address ? ` · ${data.address}` : "";
      toast.success(
        action === "check_in"
          ? `Checked in ✓${addressLine}`
          : `Checked out · ${data.hours_worked}h${addressLine}`,
      );
      if (action === "check_in" && data.face_score != null) {
        setLastMatch({ score: data.face_score, passed: data.face_passed, reason: data.face_reason });
        if (data.face_passed === false) toast.warning(`Face match low (${data.face_score}/100) — flagged for HR review`);
      }
      // Auto-prompt if the employee is still in an active trip when checking out
      if (action === "check_out" && data.active_trip) {
        const t = data.active_trip;
        if (window.confirm(`You still have an active trip (${t.mode?.toUpperCase()} from ${t.start_address || "start"}). End it now at your check-out location?`)) {
          try {
            const r = await api.post(`/trips/${t.id}/end`, { lat: loc.lat, lng: loc.lng });
            toast.success(`Trip ended · ${r.data.distance_km} km · ₹${r.data.suggested_amount || 0}`);
          } catch (te) { toast.error(formatApiError(te)); }
        }
      }
      setSelfieBlob(null); setSelfiePreview(null);
      load();
    } catch (e) {
      // Handle FACE_MATCH_REJECTED specially — clear the current selfie and prompt retake
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "FACE_MATCH_REJECTED") {
        toast.error(`Selfie mismatch (score ${detail.match_score}/100). Please retake and try again.`, { duration: 5500 });
        setSelfieBlob(null);
        setSelfiePreview(null);
        setLastMatch({ score: detail.match_score, passed: false, reason: detail.reason });
        // Auto-open camera so user can retake immediately
        try { await startVideo(); } catch { /* ignore */ }
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setBusy(false); }
  };

  const alreadyIn = !!today?.check_in_at;
  const alreadyOut = !!today?.check_out_at;
  const needsEnrollment = baseline.enrolled === false;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Self-service</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Check in for today</h1>
        <p className="text-sm text-zinc-500 mt-2">
          {new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </p>
      </div>

      {/* Baseline enrollment banner */}
      {needsEnrollment && !enrollMode && (
        <div className="border border-zenith-magenta/40 bg-zenith-magenta/5 rounded-sm p-4 flex items-start gap-3" data-testid="selfie-enroll-banner">
          <WarningCircle size={22} className="text-zenith-magenta flex-shrink-0 mt-0.5" weight="fill" />
          <div className="flex-1">
            <div className="font-display font-bold text-adb-navy text-sm">One-time face enrollment needed</div>
            <div className="text-xs text-zinc-600 mt-0.5">We compare every check-in selfie against a baseline photo of you. Enrol once, then check in normally.</div>
          </div>
          <Button onClick={() => setEnrollMode(true)} className="bg-zenith-magenta hover:brightness-110 rounded-sm text-white h-8" data-testid="selfie-enroll-open">
            Enrol now
          </Button>
        </div>
      )}

      {baseline.enrolled && !enrollMode && (
        <div className="text-xs text-emerald-700 inline-flex items-center gap-1.5" data-testid="selfie-enrolled">
          <ShieldCheck size={14} weight="fill" /> Face-match enabled · enrolled {baseline.captured_at ? new Date(baseline.captured_at).toLocaleDateString() : ""}
        </div>
      )}

      {/* Main check-in card */}
      <div className="border border-zinc-200 bg-white rounded-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              {enrollMode ? "Enrollment mode" : "Today's status"}
            </div>
            {!enrollMode && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-zinc-500">Check-in</div>
                  <div className="font-mono-plex text-lg font-semibold">{fmtTime(today?.check_in_at)}</div>
                </div>
                {today?.check_in_address && (
                  <div className="text-xs text-zinc-600 flex items-start gap-1.5"><MapPinArea size={12} className="mt-0.5 text-zenith-teal" /><span>{today.check_in_address}</span></div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-sm text-zinc-500">Check-out</div>
                  <div className="font-mono-plex text-lg font-semibold">{fmtTime(today?.check_out_at)}</div>
                </div>
                {today?.check_out_address && (
                  <div className="text-xs text-zinc-600 flex items-start gap-1.5"><MapPinArea size={12} className="mt-0.5 text-zenith-teal" /><span>{today.check_out_address}</span></div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-sm text-zinc-500">Hours worked</div>
                  <div className="font-mono-plex text-lg font-semibold">{today?.hours_worked ?? "—"}</div>
                </div>
                {today?.check_in_within_geofence === false && (
                  <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-sm px-3 py-2">
                    Off-site check-in flagged for review{today?.check_in_distance_m != null ? ` — ${Math.round(today.check_in_distance_m)} m from office` : ""}.
                  </div>
                )}
                {today?.check_in_face_score != null && (
                  <div className={`text-xs rounded-sm px-3 py-2 border ${today.check_in_face_passed === false ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`} data-testid="face-match-status">
                    Face match score: <b>{today.check_in_face_score}/100</b> — {today.check_in_face_passed === false ? "flagged for HR review" : "verified ✓"}
                  </div>
                )}
              </div>
            )}
            {enrollMode && (
              <div className="mt-4 text-xs text-zinc-600 leading-relaxed">
                Look straight into the camera, remove sunglasses/masks, ensure your face fills the frame. This photo is used to verify every future check-in.
              </div>
            )}
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Selfie{enrollMode ? " (enrollment)" : " verification"}</div>
            <div className="mt-3 aspect-[4/3] bg-zinc-100 border border-zinc-200 rounded-sm overflow-hidden grid place-items-center relative">
              {selfiePreview ? (
                <img alt="Selfie" src={selfiePreview} className="w-full h-full object-cover" />
              ) : videoOn ? (
                <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-zinc-500 text-xs px-4">
                  <Camera size={28} className="mx-auto mb-1" />
                  {enrollMode ? "Capture a clear front-facing photo" : baseline.enrolled ? "Selfie required for face-match verification." : "Selfie is optional but recommended."}
                </div>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>
            <div className="mt-2 flex gap-2 flex-wrap">
              {!videoOn && !selfiePreview && (
                <Button onClick={startVideo} variant="outline" className="rounded-sm" data-testid="selfie-open-camera">
                  <Camera size={16} className="mr-1.5" /> Open camera
                </Button>
              )}
              {videoOn && (
                <Button onClick={captureSelfie} className="rounded-sm btn-zenith" data-testid="selfie-capture">
                  <CheckCircle size={16} className="mr-1.5" /> Capture
                </Button>
              )}
              {selfiePreview && !enrollMode && (
                <Button variant="outline" onClick={() => { setSelfiePreview(null); setSelfieBlob(null); startVideo(); }} className="rounded-sm" data-testid="selfie-retake">
                  <XCircle size={16} className="mr-1.5" /> Retake
                </Button>
              )}
              {selfiePreview && enrollMode && (
                <Button variant="outline" onClick={() => { setSelfiePreview(null); setSelfieBlob(null); startVideo(); }} className="rounded-sm">
                  <XCircle size={16} className="mr-1.5" /> Retake
                </Button>
              )}
              {enrollMode && selfiePreview && (
                <Button onClick={enrollBaseline} disabled={busy} className="bg-zenith-magenta hover:brightness-110 text-white rounded-sm" data-testid="selfie-enroll-save">
                  {busy ? "Saving…" : "Save as baseline"}
                </Button>
              )}
              {enrollMode && (
                <Button variant="outline" onClick={() => { setEnrollMode(false); setSelfieBlob(null); setSelfiePreview(null); }} className="rounded-sm">Cancel</Button>
              )}
            </div>
          </div>
        </div>

        {!enrollMode && (
          <div className="mt-6 pt-6 border-t border-zinc-200">
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => doAction("check_in")}
                disabled={busy || alreadyIn}
                data-testid={ATT.checkInBtn}
                className="h-14 px-6 rounded-sm btn-zenith disabled:opacity-50"
              >
                <MapPinArea size={20} className="mr-2" weight="duotone" />
                {alreadyIn ? "Already checked in" : busy ? "Locating…" : "Check in now"}
                <ArrowRight size={16} className="ml-2" />
              </Button>
              <Button
                onClick={() => doAction("check_out")}
                disabled={busy || !alreadyIn || alreadyOut}
                data-testid={ATT.checkOutBtn}
                variant="outline"
                className="h-14 px-6 rounded-sm border-[#6D3BF5] text-[#6D3BF5] hover:bg-[#6D3BF5]/5 disabled:opacity-50"
              >
                {alreadyOut ? "Already checked out" : "Check out"}
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
            <div className="mt-3 text-xs text-zinc-500">
              Location + selfie captured every time. {baseline.enrolled ? "Every selfie is auto-compared to your baseline via AI." : "Enrol a baseline to activate face-match verification."}
            </div>
            {lastMatch && (
              <div className={`mt-3 text-xs rounded-sm px-3 py-2 inline-flex items-center gap-2 border ${lastMatch.passed ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                <ShieldCheck size={14} weight="fill" /> Match score {lastMatch.score}/100 — {lastMatch.reason || (lastMatch.passed ? "verified" : "flagged")}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border border-zinc-200 bg-white rounded-sm p-5">
        <div className="text-xs uppercase tracking-widest text-zinc-500">Last 30 days</div>
        <div className="mt-3 overflow-x-auto">
          <table className="hr-table" data-testid={ATT.monthTable}>
            <thead><tr><th>Date</th><th>In</th><th>Out</th><th>Hours</th><th>Location</th><th>Face</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-6 text-zinc-500 text-sm">Loading…</td></tr>}
              {!loading && history.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-500 text-sm">No records yet.</td></tr>}
              {history.map((a) => (
                <tr key={a.id}>
                  <td className="font-mono-plex text-xs">{a.date}</td>
                  <td className="font-mono-plex text-xs">{fmtTime(a.check_in_at)}</td>
                  <td className="font-mono-plex text-xs">{fmtTime(a.check_out_at)}</td>
                  <td className="font-mono-plex text-xs">{a.hours_worked ?? "—"}</td>
                  <td className="text-xs max-w-[220px] truncate" title={a.check_in_address_full || ""}>
                    {a.check_in_address || <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="text-xs">
                    {a.check_in_face_score != null ? (
                      <span className={a.check_in_face_passed === false ? "text-red-600" : "text-emerald-700"}>{a.check_in_face_score}</span>
                    ) : <span className="text-zinc-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
